/**
 * E029-PREFLIGHT: Pre-Flight Comparison Logic
 *
 * Pure functions for comparing local AutomationConfig with backend VerifySyncConfig.
 * Extracted from execution-panel.tsx for testability.
 *
 * SOLID:
 * - S: Single Responsibility — comparison logic only
 * - O: Open for extension — new profiles can add fields without modifying existing
 */

import {
  collectDatabaseStorageSyncDocIds,
  isServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';

import type { AutomationConfig } from '../../services/automation';
import type { VerifySyncConfig } from '../../services/automation-config-api';
import type { WorkflowSchema } from '../../services/workflow-client';

// =============================================================================
// Types
// =============================================================================

export type PreFlightProfile = 'execute' | 'test';

export interface PreFlightStep {
  name: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  durationMs?: number;
  error?: string;
}

export interface PreFlightAttribute {
  name: string;
  localValue: string;
  backendValue: string;
  match: boolean;
}

export interface PreFlightResult {
  profile: PreFlightProfile;
  steps: PreFlightStep[];
  attributes: PreFlightAttribute[];
  totalAttributes: number;
  matchedAttributes: number;
  overallSuccess: boolean;
}

export type PreFlightDisplayStatus = 'idle' | 'running' | 'done' | 'failed';

export interface StaleWorkflowMappingIds {
  input: string[];
  output: string[];
}

export interface PreFlightDatabaseStorageRef {
  tableId: string;
  docId?: string | null;
  databaseRootDocId?: string | null;
  databaseDirectoryPartDocIds?: readonly (string | null | undefined)[] | null;
  databaseRowShardDocIds?: readonly (string | null | undefined)[] | null;
}

export function getStaleWorkflowMappingIds(
  profile: PreFlightProfile,
  config: AutomationConfig,
  schema: WorkflowSchema | null | undefined
): StaleWorkflowMappingIds {
  if (!schema) {
    return { input: [], output: [] };
  }

  const inputFieldIds = new Set(
    [...schema.input.required, ...schema.input.optional].map(field => field.id)
  );
  const input = Array.from(
    new Set(
      (config.inputMappings ?? [])
        .map(mapping => mapping.schemaFieldId)
        .filter(
          (fieldId): fieldId is string =>
            !!fieldId && !inputFieldIds.has(fieldId)
        )
    )
  );

  if (profile === 'test') {
    return { input, output: [] };
  }

  const outputFieldIds = new Set(Object.keys(schema.output));
  const output = Array.from(
    new Set(
      (config.outputMappings ?? [])
        .filter(
          mapping =>
            mapping.fixedValue === undefined || mapping.fixedValue === ''
        )
        .map(mapping => mapping.responseField)
        .filter(
          (fieldId): fieldId is string =>
            !!fieldId && !outputFieldIds.has(fieldId)
        )
    )
  );

  return { input, output };
}

export function collectPreFlightDocIds(
  automationDocId: string,
  config: AutomationConfig,
  profile: PreFlightProfile,
  databaseStorageRefs: readonly PreFlightDatabaseStorageRef[] = [],
  workspaceId?: string | null
): string[] {
  const docIds = new Set<string>([automationDocId]);

  const addDocId = (docId: string | null | undefined) => {
    if (docId) {
      docIds.add(docId);
    }
  };

  // Root and default row shard are derived from workspace and table, so a
  // directory that names nothing still flushes them; a row shard this client
  // does not hold is left out by the flush.
  const addDatabaseStorageDocs = (
    tableId: string | null | undefined,
    tableDocId: string | null | undefined
  ) => {
    if (!tableId) return;
    const ref = databaseStorageRefs.find(candidate => {
      if (candidate.tableId !== tableId) return false;
      if (!tableDocId || !candidate.docId) return true;
      return candidate.docId === tableDocId;
    });
    const knownRowShardDocIds = ref?.databaseRowShardDocIds?.filter(
      (docId): docId is string => typeof docId === 'string' && docId.length > 0
    );
    const storageDocIds = collectDatabaseStorageSyncDocIds({
      databaseId: tableId,
      databaseRootDocId: ref?.databaseRootDocId,
      databaseDirectoryPartDocIds: ref?.databaseDirectoryPartDocIds,
      databaseRowShardDocIds: knownRowShardDocIds,
      workspaceId,
    });
    for (const storageDocId of storageDocIds) {
      addDocId(storageDocId);
    }
  };

  addDocId(config.sourceDocId);
  addDatabaseStorageDocs(
    config.sourceTableId,
    config.sourceDocId ?? automationDocId
  );

  for (const inputTable of config.inputTables ?? []) {
    if (inputTable.tableId && inputTable.docId) {
      addDocId(inputTable.docId);
    }
    addDatabaseStorageDocs(inputTable.tableId, inputTable.docId);
  }

  if (profile === 'execute') {
    for (const outputTable of config.outputTables ?? []) {
      if (outputTable.tableId && outputTable.docId) {
        addDocId(outputTable.docId);
      }
      addDatabaseStorageDocs(outputTable.tableId, outputTable.docId);
    }
  }

  return [...docIds];
}

/** What this client holds of the documents a preflight names. */
export type PreFlightLocalDocs = {
  workspaceId: string;
  hasMaterializedDoc: (docId: string) => boolean;
};

type PreFlightDocEngine = {
  addDoc?: (docId: string, source: 'automation-preflight') => void;
  flushAndWaitForSynced: (docId: string) => Promise<void>;
};

/**
 * Pushes the local state of every preflight document and waits for the
 * server. The deterministic row shard of a table can name a document that
 * exists nowhere (a table without rows written on this client); it has
 * nothing to push and a wait on it never ends, so it is left out.
 */
export async function flushPreFlightDocIds(
  docEngine: PreFlightDocEngine,
  docIds: readonly string[],
  localDocs: PreFlightLocalDocs
): Promise<void> {
  const uniqueDocIds = Array.from(new Set(docIds)).filter(
    docId =>
      !isServerDatabaseRowShardDocId(docId, localDocs.workspaceId) ||
      localDocs.hasMaterializedDoc(docId)
  );

  for (const docIdToFlush of uniqueDocIds) {
    docEngine.addDoc?.(docIdToFlush, 'automation-preflight');
  }
  await Promise.all(
    uniqueDocIds.map(docIdToFlush =>
      docEngine.flushAndWaitForSynced(docIdToFlush)
    )
  );
}

/** The documents a preflight flushes, as {@link collectPreFlightDocIds} names them. */
export interface PreFlightDocSet {
  automationDocId: string;
  config: AutomationConfig;
  profile: PreFlightProfile;
  /** Database storage as discovery saw it; the directory is read again. */
  databaseStorageRefs: readonly PreFlightDatabaseStorageRef[];
  workspaceId?: string | null;
}

/** The stored shard directories of the databases a preflight names. */
export type PreFlightDatabaseDirectories = {
  /**
   * Directory part documents and the row shards of the loaded parts as the
   * database stores them now; undefined while its host is not loaded.
   */
  read: (
    ref: PreFlightDatabaseStorageRef
  ) =>
    | { partDocIds: readonly string[]; shardDocIds: readonly string[] }
    | undefined;
  /** Whether the server confirmed a document this client created. */
  waitForServerConfirmed: (docId: string) => Promise<boolean>;
};

/**
 * Flushes the documents of a preflight. The row-shard list of a database
 * lives in its directory part documents, and parts can arrive after
 * discovery: the parts go first and must be confirmed by the server, then the
 * directory is read again and the row shards it names now are flushed.
 */
export async function flushPreFlightDocs(
  docEngine: PreFlightDocEngine,
  docSet: PreFlightDocSet,
  localDocs: PreFlightLocalDocs,
  directories: PreFlightDatabaseDirectories
): Promise<void> {
  const collect = () => {
    const refs = docSet.databaseStorageRefs.map(ref => {
      const directory = directories.read(ref);
      return directory
        ? {
            ...ref,
            databaseDirectoryPartDocIds: directory.partDocIds,
            databaseRowShardDocIds: directory.shardDocIds,
          }
        : ref;
    });
    const docIds = collectPreFlightDocIds(
      docSet.automationDocId,
      docSet.config,
      docSet.profile,
      refs,
      docSet.workspaceId
    );
    const partDocIds = new Set(
      refs.flatMap(ref => ref.databaseDirectoryPartDocIds ?? [])
    );
    return {
      docIds,
      partDocIds: docIds.filter(docId => partDocIds.has(docId)),
    };
  };

  const parts = collect();
  await flushPreFlightDocIds(docEngine, parts.docIds, localDocs);
  const confirmed = await Promise.all(
    parts.partDocIds.map(docId => directories.waitForServerConfirmed(docId))
  );
  const unconfirmed = parts.partDocIds.filter((_, index) => !confirmed[index]);
  if (unconfirmed.length > 0) {
    throw new Error(
      `The server did not confirm the database directory documents ${unconfirmed.join(', ')}.`
    );
  }

  const flushed = new Set(parts.docIds);
  const shards = collect().docIds.filter(docId => !flushed.has(docId));
  if (shards.length > 0) {
    await flushPreFlightDocIds(docEngine, shards, localDocs);
  }
}

// =============================================================================
// Comparison Helpers
// =============================================================================

/** Format a value for display in the attribute table */
function fmtObj(val: unknown): string {
  if (val === null || val === undefined) return '(none)';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/**
 * Classify filter state into 3 categories:
 * - 'none': null/undefined (no filter configured)
 * - 'empty': object exists but no conditions (default shell)
 * - 'configured': has actual filter conditions
 */
type FilterState = 'none' | 'empty' | 'configured';

function classifyFilter(fg: unknown): FilterState {
  if (!fg || typeof fg !== 'object') return 'none';
  const conditions = (fg as { conditions?: unknown[] }).conditions;
  if (!Array.isArray(conditions) || conditions.length === 0) return 'empty';
  return 'configured';
}

function getFilterConditionCount(fg: unknown): number {
  if (!fg || typeof fg !== 'object') return 0;
  const conditions = (fg as { conditions?: unknown[] }).conditions;
  return Array.isArray(conditions) ? conditions.length : 0;
}

function normalizeInputMappings(
  mappings:
    | AutomationConfig['inputMappings']
    | VerifySyncConfig['inputMappings']
) {
  return (mappings ?? [])
    .map(mapping => ({
      schemaFieldId: mapping.schemaFieldId,
      columnId: mapping.columnId ?? null,
      ...(mapping.inputTableId ? { inputTableId: mapping.inputTableId } : {}),
    }))
    .sort(
      (a, b) =>
        [
          a.schemaFieldId.localeCompare(b.schemaFieldId),
          (a.inputTableId ?? '').localeCompare(b.inputTableId ?? ''),
          (a.columnId ?? '').localeCompare(b.columnId ?? ''),
        ].find(result => result !== 0) ?? 0
    );
}

function normalizeInputTables(
  tables: AutomationConfig['inputTables'] | VerifySyncConfig['inputTables']
) {
  return (tables ?? [])
    .map(table => ({
      id: table.id,
      tableId: table.tableId,
      tableName: table.tableName,
      ...(table.docId ? { docId: table.docId } : {}),
      role: table.role,
      ...(table.filterGroup ? { filterGroup: table.filterGroup } : {}),
      ...(table.linkedVia ? { linkedVia: table.linkedVia } : {}),
      ...(table.cardinality ? { cardinality: table.cardinality } : {}),
      ...(table.multiFieldName ? { multiFieldName: table.multiFieldName } : {}),
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

function normalizeOutputTables(
  tables: AutomationConfig['outputTables'] | VerifySyncConfig['outputTables']
) {
  return (tables ?? [])
    .map(table => ({
      tableId: table.tableId,
      docId: table.docId,
      ...(table.mode ? { mode: table.mode } : {}),
      ...(table.matchConfigId ? { matchConfigId: table.matchConfigId } : {}),
    }))
    .sort(
      (a, b) =>
        [
          a.tableId.localeCompare(b.tableId),
          a.docId.localeCompare(b.docId),
          (a.mode ?? '').localeCompare(b.mode ?? ''),
          (a.matchConfigId ?? '').localeCompare(b.matchConfigId ?? ''),
        ].find(result => result !== 0) ?? 0
    );
}

type MatchConfigLike = {
  id?: unknown;
  targetTableId?: unknown;
  multiMatchBehavior?: unknown;
  notFoundBehavior?: unknown;
  stealBehavior?: unknown;
  keys?: unknown;
};

type MatchKeyLike = {
  columnId?: unknown;
  sourceField?: unknown;
  matchMode?: unknown;
};

function strOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeMatchConfigs(
  configs: AutomationConfig['matchConfigs'] | VerifySyncConfig['matchConfigs']
) {
  return (configs ?? [])
    .map(config => {
      const cfg = config as MatchConfigLike;
      const keys = Array.isArray(cfg.keys)
        ? cfg.keys
            .map(key => {
              const matchKey = key as MatchKeyLike;
              return {
                columnId: strOrEmpty(matchKey.columnId),
                sourceField: strOrEmpty(matchKey.sourceField),
                matchMode: strOrEmpty(matchKey.matchMode),
              };
            })
            .sort(
              (a, b) =>
                [
                  a.columnId.localeCompare(b.columnId),
                  a.sourceField.localeCompare(b.sourceField),
                  a.matchMode.localeCompare(b.matchMode),
                ].find(result => result !== 0) ?? 0
            )
        : [];

      return {
        id: strOrEmpty(cfg.id),
        targetTableId: strOrEmpty(cfg.targetTableId),
        multiMatchBehavior: strOrEmpty(cfg.multiMatchBehavior),
        ...(cfg.notFoundBehavior
          ? { notFoundBehavior: strOrEmpty(cfg.notFoundBehavior) }
          : {}),
        ...(cfg.stealBehavior
          ? { stealBehavior: strOrEmpty(cfg.stealBehavior) }
          : {}),
        keys,
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
}

/** Build attribute comparisons for Execute profile */
export function buildExecuteAttributes(
  local: AutomationConfig,
  backend: VerifySyncConfig
): PreFlightAttribute[] {
  const localOutputTables = normalizeOutputTables(local.outputTables);
  const backendOutputTables = normalizeOutputTables(backend.outputTables);
  const localOutputTablesStr = JSON.stringify(localOutputTables);
  const backendOutputTablesStr = JSON.stringify(backendOutputTables);

  // Match backend normalization exactly (config-sync.service.ts readBlockConfig):
  // columnId uses `?? undefined` so null/undefined are stringified identically
  // (JSON.stringify drops undefined keys — otherwise null vs undefined produces
  // a false positive mismatch).
  const localStatusTracking = local.statusTracking?.enabled
    ? {
        enabled: true,
        ...(local.statusTracking.mode === 'delete_row'
          ? { mode: 'delete_row' }
          : {}),
        columnId: local.statusTracking.columnId ?? undefined,
        successValue: local.statusTracking.successValue,
      }
    : null;
  const localSTStr = JSON.stringify(localStatusTracking);
  const backendSTStr = JSON.stringify(backend.statusTracking);

  const localTrigger = local.triggerConfig
    ? {
        type: local.triggerConfig.type,
        enabled: local.triggerConfig.enabled,
        cronPreset: local.triggerConfig.cronPreset,
      }
    : null;
  const localTriggerStr = JSON.stringify(localTrigger);
  const backendTriggerStr = JSON.stringify(backend.triggerConfig);

  const localBatch = local.batchConfig
    ? {
        batchSize: local.batchConfig.batchSize,
        executionMode: local.batchConfig.executionMode ?? 'row',
      }
    : null;
  const localBatchStr = JSON.stringify(localBatch);
  const backendBatchStr = JSON.stringify(backend.batchConfig);

  // Filter comparison uses 3-state classification because local (BlockSuite)
  // and backend use different filter schemas — JSON.stringify can't be compared.
  // `'none'` (null/undefined) and `'empty'` (shell object with zero conditions)
  // are semantically equivalent: no filter is applied either way. Treating
  // them as a mismatch produced spurious Pre-Flight failures where local and
  // backend both displayed `(none)` but differed only in the wire-format
  // shell — a pure serialization artefact with no runtime effect.
  const localFilterState = classifyFilter(local.filterGroup);
  const backendFilterState = classifyFilter(backend.filterConfig);
  const isInactiveFilter = (s: FilterState) => s === 'none' || s === 'empty';
  const filterMatch = isInactiveFilter(localFilterState)
    ? isInactiveFilter(backendFilterState)
    : localFilterState === backendFilterState &&
      getFilterConditionCount(local.filterGroup) ===
        getFilterConditionCount(backend.filterConfig);

  const localMappingsStr = local.outputMappings?.length
    ? JSON.stringify(local.outputMappings)
    : null;
  const backendMappingsStr = backend.outputMappings?.length
    ? JSON.stringify(backend.outputMappings)
    : null;

  const localSelectedCols = [...(local.selectedColumns ?? [])].sort();
  const localInputMappings = normalizeInputMappings(local.inputMappings);
  const backendInputMappings = normalizeInputMappings(backend.inputMappings);
  const localInputTables = normalizeInputTables(local.inputTables);
  const backendInputTables = normalizeInputTables(backend.inputTables);
  const localMatchConfigs = normalizeMatchConfigs(local.matchConfigs);
  const backendMatchConfigs = normalizeMatchConfigs(backend.matchConfigs);
  const localInputMappingsStr = JSON.stringify(localInputMappings);
  const backendInputMappingsStr = JSON.stringify(backendInputMappings);
  const localInputTablesStr = JSON.stringify(localInputTables);
  const backendInputTablesStr = JSON.stringify(backendInputTables);
  const localMatchConfigsStr = JSON.stringify(localMatchConfigs);
  const backendMatchConfigsStr = JSON.stringify(backendMatchConfigs);

  return [
    {
      name: 'sourceTableId',
      localValue: local.sourceTableId ?? '(none)',
      backendValue: backend.sourceTableId ?? '(none)',
      match: (local.sourceTableId ?? null) === (backend.sourceTableId ?? null),
    },
    {
      name: 'sourceDocId',
      localValue: local.sourceDocId ?? '(none)',
      backendValue: backend.sourceDocId ?? '(none)',
      match: (local.sourceDocId ?? null) === (backend.sourceDocId ?? null),
    },
    {
      name: 'workflowId',
      localValue: local.workflowId ?? '(none)',
      backendValue: backend.workflowId ?? '(none)',
      match: (local.workflowId ?? null) === (backend.workflowId ?? null),
    },
    {
      name: 'outputTables',
      localValue: `${localOutputTables.length} tables`,
      backendValue: `${backend.outputTables.length} tables`,
      match: localOutputTablesStr === backendOutputTablesStr,
    },
    {
      name: 'outputMappings',
      localValue: `${local.outputMappings?.length ?? 0} mappings`,
      backendValue: `${backend.outputMappings?.length ?? 0} mappings`,
      match: localMappingsStr === backendMappingsStr,
    },
    {
      name: 'matchConfigs',
      localValue: `${localMatchConfigs.length} configs`,
      backendValue: `${backendMatchConfigs.length} configs`,
      match: localMatchConfigsStr === backendMatchConfigsStr,
    },
    {
      name: 'statusTracking',
      localValue: fmtObj(localStatusTracking),
      backendValue: fmtObj(backend.statusTracking),
      match: localSTStr === backendSTStr,
    },
    {
      name: 'triggerConfig',
      localValue: fmtObj(localTrigger),
      backendValue: fmtObj(backend.triggerConfig),
      match: localTriggerStr === backendTriggerStr,
    },
    {
      name: 'batchConfig',
      localValue: fmtObj(localBatch),
      backendValue: fmtObj(backend.batchConfig),
      match: localBatchStr === backendBatchStr,
    },
    {
      name: 'filterConfig',
      localValue: localFilterState === 'configured' ? 'configured' : '(none)',
      backendValue:
        backendFilterState === 'configured' ? 'configured' : '(none)',
      match: filterMatch,
    },
    {
      name: 'selectedColumns',
      localValue: `${localSelectedCols.length} cols`,
      backendValue: `${backend.selectedColumnsCount} cols`,
      match: localSelectedCols.join(',') === backend.selectedColumns.join(','),
    },
    {
      name: 'inputMappings',
      localValue: `${localInputMappings.length} mappings`,
      backendValue: `${backendInputMappings.length} mappings`,
      match: localInputMappingsStr === backendInputMappingsStr,
    },
    {
      name: 'inputTables',
      localValue: `${localInputTables.length} tables`,
      backendValue: `${backendInputTables.length} tables`,
      match: localInputTablesStr === backendInputTablesStr,
    },
    {
      name: 'inputTablesCount',
      localValue: String(local.inputTables?.length ?? 0),
      backendValue: String(backend.inputTablesCount),
      match: (local.inputTables?.length ?? 0) === backend.inputTablesCount,
    },
    {
      name: 'outputTablesCount',
      localValue: String(local.outputTables?.length ?? 0),
      backendValue: String(backend.outputTablesCount),
      match: (local.outputTables?.length ?? 0) === backend.outputTablesCount,
    },
    {
      name: 'outputMappingsCount',
      localValue: String(local.outputMappings?.length ?? 0),
      backendValue: String(backend.outputMappingsCount),
      match:
        (local.outputMappings?.length ?? 0) === backend.outputMappingsCount,
    },
    {
      name: 'selectedColumnsCount',
      localValue: String(localSelectedCols.length),
      backendValue: String(backend.selectedColumnsCount),
      match: localSelectedCols.length === backend.selectedColumnsCount,
    },
  ];
}

export function buildTestAttributes(
  local: AutomationConfig,
  backend: VerifySyncConfig
): PreFlightAttribute[] {
  return buildExecuteAttributes(local, backend).filter(
    attr =>
      attr.name !== 'outputTables' &&
      attr.name !== 'outputMappings' &&
      attr.name !== 'matchConfigs' &&
      attr.name !== 'outputTablesCount' &&
      attr.name !== 'outputMappingsCount' &&
      attr.name !== 'statusTracking' &&
      attr.name !== 'triggerConfig'
  );
}

export function buildPreFlightAttributes(
  profile: PreFlightProfile,
  local: AutomationConfig,
  backend: VerifySyncConfig
): PreFlightAttribute[] {
  if (profile === 'test') return buildTestAttributes(local, backend);
  return buildExecuteAttributes(local, backend);
}

/** Evaluate a list of attributes and build a PreFlightResult */
export function evaluatePreFlightResult(
  profile: PreFlightProfile,
  steps: PreFlightStep[],
  attributes: PreFlightAttribute[]
): PreFlightResult {
  const matchedAttributes = attributes.filter(a => a.match).length;
  return {
    profile,
    steps,
    attributes,
    totalAttributes: attributes.length,
    matchedAttributes,
    overallSuccess:
      matchedAttributes === attributes.length &&
      steps.every(s => s.status === 'done'),
  };
}

export function getPreFlightDisplayStatus(
  result: PreFlightResult | null
): PreFlightDisplayStatus {
  if (!result) return 'idle';
  if (result.steps.some(step => step.status === 'failed')) return 'failed';
  if (result.overallSuccess) return 'done';
  return 'running';
}
